import { useCallback, useEffect, useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { toast } from "sonner";
import { HistoryChart } from "@/components/dados/HistoryChart";
import { Database, Plus, Copy, Trash2, RefreshCw, DollarSign, Pencil, ChevronRight, Radio } from "lucide-react";

const usd = (v) => (v == null ? "—" : `US$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const num = (v) => Number(v || 0).toLocaleString("pt-BR");
const PANEL_COLORS = { csalpha: "#38BDF8", alpha2: "#10B981", wetester: "#F59E0B", alpha4: "#A78BFA" };

const InlineEdit = ({ testid, value, onSave, step = "1", prefix, width = "w-20" }) => {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <label className="group relative inline-flex items-center gap-1 rounded-md border border-slate-700/50 bg-slate-800/40 px-2 py-0.5 font-mono text-sm font-semibold text-slate-200 transition-colors hover:border-slate-500 hover:bg-slate-800/80 focus-within:border-sky-500 focus-within:ring-1 focus-within:ring-sky-500/30">
      {prefix && <span className="text-[11px] text-slate-500">{prefix}</span>}
      <input data-testid={testid} type="number" step={step} value={v ?? ""} onChange={(e) => setV(e.target.value)}
        onBlur={() => String(v) !== String(value) && onSave(v === "" ? null : Number(v))}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className={`${width} bg-transparent text-right outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none`} />
      <Pencil className="h-3 w-3 text-slate-600 transition-colors group-hover:text-sky-400" />
    </label>
  );
};

const Delta = ({ v, fmt }) => v == null || Math.abs(v) < 0.005 ? null : (
  <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${v > 0 ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>{v > 0 ? "+" : "−"}{fmt(Math.abs(v))}</span>
);

const Step = ({ testid, label, value, tone = "text-white", sub, arrow }) => (
  <div className="relative flex items-stretch">
    <div data-testid={testid} className="min-w-0 flex-1 rounded-xl border border-[#1F293D] bg-[#111827]/80 px-4 py-4 transition-colors hover:border-slate-600/60">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 whitespace-nowrap font-mono text-2xl font-extrabold tracking-tight ${tone}`}>{value}</div>
      {sub && <div className="mt-0.5 truncate text-[11px] text-slate-500">{sub}</div>}
    </div>
    {arrow && <ChevronRight className="absolute -right-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 rounded-full bg-[#0F141F] text-slate-500" />}

  </div>
);

const PanelBars = ({ testid, panels, d, edit }) => {
  const max = Math.max(1, ...panels.map((p) => d.qty[p.account] || 0));
  return (
    <div className="space-y-2">
      {panels.map((p) => (
        <div key={p.account} data-testid={`${testid}-qty-${p.account}`}>
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-2 text-slate-400"><span className="h-2 w-2 rounded-full" style={{ background: PANEL_COLORS[p.account] }} /><b className="font-semibold text-slate-200">{p.account}</b></span>
            {edit ? edit(p.account) : <span className="font-mono font-bold text-white">{num(d.qty[p.account])}</span>}
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[#0B0F17]">
            <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${(100 * (d.qty[p.account] || 0)) / max}%`, background: PANEL_COLORS[p.account] }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const Hero = ({ d, c, panels, fx, save }) => {
  const a = d.atual;
  const pct = Math.min(100, (100 * a.total) / Math.max(1, c.meta));
  return (
    <div data-testid="dados-atual" className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-[#0F141F] p-6 shadow-2xl shadow-black/40 fade-up">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="relative grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-300"><Radio className="h-3.5 w-3.5 animate-pulse" /> Atual · ao vivo</div>
          <div className="mt-3 flex items-end gap-3">
            <span data-testid="dados-atual-total" className="font-mono text-6xl font-extrabold tracking-tighter text-white">{num(a.total)}</span>
            <span className="mb-2 text-sm text-slate-400">clientes ativos</span>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs">
              <span data-testid="dados-atual-falta" className="text-slate-400">Faltam <b className="font-mono text-rose-300">{num(a.falta)}</b> para a meta</span>
              <span className="flex items-center gap-2 text-slate-500">meta <InlineEdit testid="dados-meta" value={c.meta} onSave={(v) => save({ meta: v })} /></span>
            </div>
            <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-[#0B0F17]">
              <div className="h-full rounded-full bg-gradient-to-r from-amber-500 via-amber-300 to-emerald-400 transition-[width] duration-700" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-1 text-right font-mono text-[11px] text-slate-500">{pct.toFixed(1)}%</div>
          </div>
          <div className="mt-4"><PanelBars testid="dados-atual" panels={panels} d={a} /></div>
        </div>
        <div className="flex flex-col gap-4 lg:col-span-7">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[["dados-atual-valor", "Valor por cliente", <InlineEdit key="v" testid="dados-valor" value={c.valor} step="0.01" prefix="R$" onSave={(v) => save({ valor: v })} />],
              ["dados-atual-custo1", "Custo 1 por cliente", <InlineEdit key="c1" testid="dados-custo1-unit" value={c.custo1_unit} step="0.01" prefix="R$" onSave={(v) => save({ custo1_unit: v })} />],
              ["dados-atual-custo2", "Custo 2 fixo", <InlineEdit key="c2" testid="dados-custo2" value={c.custo2} step="0.01" prefix="R$" width="w-24" onSave={(v) => save({ custo2: v })} />],
              ["dados-atual-dolar", "Dólar do dia", <span key="d" className="font-mono text-sm font-bold text-emerald-300">{fx.rate ? `R$ ${fx.rate.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}` : "—"}</span>]].map(([t, l, el]) => (
              <div key={t} data-testid={t} className="rounded-xl border border-[#1F293D] bg-[#111827]/70 px-3 py-2.5">
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{l}</div>{el}
              </div>
            ))}
          </div>
          <div className="grid flex-1 grid-cols-2 gap-3">
            <Step testid="dados-atual-bruto" label="Bruto" value={fmtBRL(a.bruto)} sub={`${num(a.total)} × ${fmtBRL(a.valor)}`} arrow />
            <Step testid="dados-atual-custo-total" label="Custo total" value={`− ${fmtBRL(a.custo_total)}`} tone="text-rose-300" sub={`custo 1 ${fmtBRL(a.custo1)} + custo 2 ${fmtBRL(a.custo2)}`} />
            <Step testid="dados-atual-liquido" label="Líquido" value={fmtBRL(a.liquido)} tone="text-emerald-300" sub={`margem ${a.bruto ? ((100 * a.liquido) / a.bruto).toFixed(1) : 0}%`} arrow />
            <Step testid="dados-atual-cambio" label="Câmbio" value={usd(a.cambio)} tone="text-sky-300" sub={`÷ R$ ${a.dolar ? a.dolar.toLocaleString("pt-BR", { maximumFractionDigits: 4 }) : "—"}`} />
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ testid, label, children, strong }) => (
  <div data-testid={testid} className="flex items-center justify-between py-1.5 text-sm">
    <span className={strong ? "font-semibold text-slate-200" : "text-slate-400"}>{label}</span>
    <span className={`font-mono ${strong ? "font-bold text-white" : "text-slate-200"}`}>{children}</span>
  </div>
);

const ScenarioCard = ({ i, s, sc, c, d, panels, setScen, dup, del }) => {
  const t = `dados-scen-${i}`;
  const a = d.atual;
  return (
    <div data-testid={t} className="flex w-[340px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-[#1F293D] bg-[#111827] shadow-xl shadow-black/40 transition-colors hover:border-slate-600/60 fade-up">
      <div className="flex items-center gap-2 border-b border-[#1F293D] bg-gradient-to-r from-sky-500/10 to-transparent px-4 py-3">
        <input data-testid={`${t}-name`} defaultValue={s.name} onBlur={(e) => e.target.value !== s.name && setScen(i, { name: e.target.value })}
          className="min-w-0 flex-1 rounded bg-transparent text-sm font-bold text-white outline-none focus:bg-slate-800/60 focus:px-1" />
        <button data-testid={`${t}-live`} onClick={() => setScen(i, { live: !sc.live, qty: sc.qty || { ...a.qty } })}
          title={sc.live ? "Usando quantidades atuais — clique para editar à mão" : "Usar quantidades atuais em tempo real"}
          className={`rounded-full px-2 py-0.5 text-[10px] font-bold transition-colors ${sc.live ? "bg-amber-500/15 text-amber-300" : "bg-slate-700/40 text-slate-400 hover:text-white"}`}>{sc.live ? "● AO VIVO" : "MANUAL"}</button>
        <button data-testid={`${t}-dup`} title="Duplicar" onClick={dup} className="rounded p-1 text-slate-500 transition-colors hover:bg-slate-800 hover:text-white"><Copy className="h-3.5 w-3.5" /></button>
        <button data-testid={`${t}-del`} title="Remover" onClick={del} className="rounded p-1 text-slate-500 transition-colors hover:bg-rose-500/10 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <div className="space-y-4 p-4">
        <PanelBars testid={t} panels={panels} d={s} edit={sc.live ? null : (acc) => <InlineEdit testid={`${t}-qty-input-${acc}`} value={sc.qty?.[acc] ?? 0} width="w-14" onSave={(v) => setScen(i, { qty: { ...sc.qty, [acc]: v || 0 } })} />} />
        <div className="rounded-xl bg-[#0F141F] px-3 py-2">
          <Row testid={`${t}-total`} label="Total de clientes" strong>{num(s.total)}<Delta v={s.total - a.total} fmt={num} /></Row>
          <Row testid={`${t}-falta`} label="Falta pra meta (hoje)"><span className="flex items-center gap-2"><span className="text-rose-300">{num(s.falta)}</span><InlineEdit testid={`${t}-meta`} value={sc.meta} width="w-14" onSave={(v) => setScen(i, { meta: v })} /></span></Row>
        </div>
        <div className="divide-y divide-[#1F293D]/70">
          <Row testid={`${t}-valor`} label="Valor"><InlineEdit testid={`${t}-valor-input`} value={sc.valor} step="0.01" prefix="R$" width="w-16" onSave={(v) => setScen(i, { valor: v })} /></Row>
          <Row testid={`${t}-bruto`} label="Bruto">{fmtBRL(s.bruto)}</Row>
          <Row testid={`${t}-custo1`} label="Custo 1"><span className="flex items-center gap-2">{fmtBRL(s.custo1)}<InlineEdit testid={`${t}-custo1-unit`} value={sc.custo1_unit ?? c.custo1_unit} step="0.01" width="w-12" onSave={(v) => setScen(i, { custo1_unit: v })} /></span></Row>
          <Row testid={`${t}-custo2`} label="Custo 2"><InlineEdit testid={`${t}-custo2-input`} value={sc.custo2} step="0.01" prefix="R$" width="w-20" onSave={(v) => setScen(i, { custo2: v })} /></Row>
          <Row testid={`${t}-custo-total`} label="Custo total" strong><span className="text-rose-300">{fmtBRL(s.custo_total)}</span></Row>
        </div>
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-3 py-2">
          <Row testid={`${t}-liquido`} label="Líquido" strong><span className="text-emerald-300">{fmtBRL(s.liquido)}</span><Delta v={s.liquido - a.liquido} fmt={fmtBRL} /></Row>
        </div>
      </div>
      <div className="mt-auto flex items-center justify-between border-t border-[#1F293D] bg-[#0F141F] px-4 py-3">
        <div data-testid={`${t}-dolar`} className="flex items-center gap-2 text-xs text-slate-400">
          <select data-testid={`${t}-dolar-mode`} value={sc.dolar_mode || "dia"} onChange={(e) => setScen(i, { dolar_mode: e.target.value })}
            className="rounded-md border border-slate-700/60 bg-[#111827] px-1.5 py-0.5 text-xs text-slate-200 outline-none focus:border-sky-500">
            <option value="dia">dólar do dia</option><option value="fixo">dólar fixo</option>
          </select>
          {sc.dolar_mode === "fixo" ? <InlineEdit testid={`${t}-dolar-input`} value={sc.dolar} step="0.01" prefix="R$" width="w-12" onSave={(v) => setScen(i, { dolar: v })} /> : <span className="font-mono text-slate-200">R$ {s.dolar?.toLocaleString("pt-BR", { maximumFractionDigits: 3 })}</span>}
        </div>
        <div data-testid={`${t}-cambio`} className="whitespace-nowrap text-right font-mono text-sm font-extrabold text-sky-300">{usd(s.cambio)}</div>
      </div>
    </div>
  );
};

export default function Dados() {
  const [d, setD] = useState(null);
  const [spin, setSpin] = useState(false);
  const load = useCallback(() => api.get("/dados").then((r) => setD(r.data)).catch(() => toast.error("Erro ao carregar Dados")), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  if (!d) return <div data-testid="dados-loading" className="py-20 text-center text-slate-500">Carregando...</div>;
  const save = async (patch) => {
    try { const r = await api.put("/dados/config", patch); setD(r.data); toast.success("Salvo"); }
    catch { toast.error("Não foi possível salvar"); }
  };
  const c = d.config;
  const scen = c.scenarios || [];
  const setScen = (i, patch) => save({ scenarios: scen.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const newScen = (base) => save({ scenarios: [...scen, base || { name: `Meta ${scen.length + 1}`, qty: { ...d.atual.qty }, meta: c.meta, valor: c.valor, custo1_unit: c.custo1_unit, custo2: c.custo2, dolar_mode: "dia", dolar: 6 }] });
  const refresh = async () => { setSpin(true); await load(); setTimeout(() => setSpin(false), 500); };
  return (
    <div data-testid="dados-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="rounded-xl bg-amber-500/10 p-2"><Database className="h-7 w-7 text-amber-300" /></span> Dados
          </h1>
          <p className="mt-2 text-sm text-slate-400">Clientes por painel, receita e câmbio em tempo real · clique nos valores com <Pencil className="inline h-3 w-3" /> para editar</p>
        </div>
        <div data-testid="dados-fx" className="flex items-center gap-3 rounded-2xl border border-emerald-500/20 bg-[#111827] px-4 py-2.5 shadow-lg shadow-emerald-900/10">
          <span className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10">
            <DollarSign className="h-5 w-5 text-emerald-400" />
            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 animate-ping rounded-full bg-emerald-400" />
          </span>
          <div>
            <div className="font-mono text-xl font-extrabold text-white">{d.fx.rate ? `R$ ${d.fx.rate.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}` : "—"}</div>
            <div className="text-[11px] text-slate-500">USD/BRL · {d.fx.source || "sem fonte"} · {d.fx.at ? new Date(d.fx.at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : ""}</div>
          </div>
          <button data-testid="dados-refresh" onClick={refresh} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-[#1A2234] hover:text-white"><RefreshCw className={`h-4 w-4 ${spin ? "animate-spin" : ""}`} /></button>
        </div>
      </div>
      <Hero d={d} c={c} panels={d.panels} fx={d.fx} save={save} />
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-300">Simulações</h3>
          <span className="text-xs text-slate-500">diferenças em relação ao atual</span>
        </div>
        <div className="flex snap-x gap-4 overflow-x-auto pb-3">
          {d.scenarios.map((s, i) => (
            <ScenarioCard key={i} i={i} s={s} sc={scen[i]} c={c} d={d} panels={d.panels} setScen={setScen}
              dup={() => newScen({ ...scen[i], name: `${s.name} (cópia)` })}
              del={() => window.confirm(`Remover "${s.name}"?`) && save({ scenarios: scen.filter((_, j) => j !== i) })} />
          ))}
          <button data-testid="dados-add-scenario" onClick={() => newScen()} className="flex w-[200px] shrink-0 snap-start flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[#1F293D] text-sm text-slate-400 transition-colors hover:border-sky-500/50 hover:bg-sky-500/5 hover:text-white">
            <Plus className="h-6 w-6" /> Nova simulação
          </button>
        </div>
      </div>
      <HistoryChart />
    </div>
  );
}
